// @flow

/* eslint-disable-next-line no-unused-vars */
import React, { Fragment, type Node, useEffect } from 'react';

import TrialWelcomeModalHeader from '@zapier/design-system-company/src/components/TrialWelcomeModalHeader';
import { DateTime } from '@zapier/date';
import { usePaymentSource } from '@zapier/payment';
import {
  Animation,
  Button,
  Colors,
  Heading,
  Spacer,
  Text,
} from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  responsiveStyles,
} from '@zapier/common-utils';

import useAutoUpgrade from './useAutoUpgrade';

import { type Size } from '@zapier/design-system-company/src/types';

/** @jsx isolateComponent */

type Props = {
  /** The current account id (for submitting survey) */
  accountId: number,
  /** Call to change modal route */
  onRoute: string => void,
  /** Payment App */
  paymentApp: Node,
  /** Size */
  size: Size,
  /** End date of trial */
  trialEndDate: string,
};

const Styles = labelStyles(__filename, {
  body: size =>
    responsiveStyles(size, {
      small: {
        display: 'grid',
        gridGap: 20,
        margin: 20,
        position: 'relative',
      },
      medium: {
        margin: 40,
      },
    }),
  heading: {
    padding: '44px 0',
    textAlign: 'center',
  },
  link: {
    color: Colors.black,
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'center',
    transition: Animation.transitionValue,

    ':hover': {
      color: Colors.blue500,
    },
  },
});

const TrialWelcomeModalPaymentView = (props: Props) => {
  const { error, hasSaved, isLoading, onEnableAutoUpgrade } = useAutoUpgrade({
    accountId: props.accountId,
  });

  const { forceSourceEdit, primarySource } = usePaymentSource({
    accountId: props.accountId,
  });

  useEffect(() => {
    if (!error && !isLoading && hasSaved) {
      props.onRoute('guide');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, hasSaved, isLoading]);

  const handleEnable = () => {
    onEnableAutoUpgrade();
  };

  return (
    <Fragment>
      <TrialWelcomeModalHeader>
        <div css={Styles.heading}>
          <Heading>
            <Text color="white" type="subtitle900">
              Enable autoupgrade
            </Text>
          </Heading>
          <Spacer height={8} />
          <Text color="white" type="bodyText900">
            Avoid disruption to your account. Cancel anytime.
          </Text>
        </div>
      </TrialWelcomeModalHeader>
      <div css={Styles.body(props.size)}>
        {props.paymentApp}
        <Text textAlign="center" type="altText900">
          Cancel anytime. You will not be charged until{' '}
          <DateTime date={props.trialEndDate} />
        </Text>
        <Button
          disabled={!primarySource || forceSourceEdit}
          isFullWidth={true}
          onClick={handleEnable}
        >
          Enable Autoupgrade
        </Button>
        <button css={Styles.link} onClick={() => props.onRoute('guide')}>
          Setup Later
        </button>
      </div>
    </Fragment>
  );
};

export default TrialWelcomeModalPaymentView;
